import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { Activity, Terminal, DollarSign, PieChart, ActivitySquare } from "lucide-react";

export function OptionsWorkspace() {
  const [portfolio, setPortfolio] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      try {
        const portRes = await axios.get(`${API}/options/portfolio`);
        const logRes = await axios.get(`${API}/options/logs?lines=50`);
        if (alive) {
          setPortfolio(portRes.data);
          setLogs(logRes.data.logs || []);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch options data", err);
      }
    };
    
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-xl font-light text-gray-500 animate-pulse flex items-center gap-3">
          <Activity className="animate-spin text-blue-500" /> Connecting to Atlas Derivatives Engine...
        </div>
      </div>
    );
  }

  const isOnline = portfolio?.status === "online";

  return (
    <div className="p-8 h-full w-full overflow-y-auto" style={{ background: "linear-gradient(to bottom, #0a0c0f, #050608)" }}>
      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 drop-shadow-sm mb-1">
            Derivatives Engine
          </h1>
          <p className="text-sm font-medium text-gray-400 tracking-wide">
            VOLATILITY CRUSH & EXPANSION PIPELINE
          </p>
        </div>
        
        <div className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest shadow flex items-center gap-2 ${
          isOnline ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-500/30' : 'bg-red-900/40 text-red-400 border border-red-500/30'
        }`}>
          <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}></div>
          {isOnline ? 'Daemon Active' : 'Daemon Offline'}
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <MetricCard 
          title="Total Equity" 
          value={portfolio ? `$${(portfolio.equity || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}` : "—"} 
          icon={<PieChart size={20} />} 
          color="blue"
        />
        <MetricCard 
          title="Available Cash" 
          value={portfolio ? `$${(portfolio.cash || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}` : "—"} 
          icon={<DollarSign size={20} />} 
          color="emerald"
        />
        <MetricCard 
          title="Unrealized PnL" 
          value={portfolio ? `$${((portfolio.live_unrealized_pnl || 0)*100).toLocaleString(undefined, {minimumFractionDigits: 2})}` : "—"} 
          icon={<Activity size={20} />} 
          color={(portfolio?.live_unrealized_pnl || 0) >= 0 ? "emerald" : "red"}
          glow
        />
        <MetricCard 
          title="Net Portfolio Vega" 
          value={portfolio ? (portfolio.net_vega || 0).toFixed(4) : "—"} 
          icon={<ActivitySquare size={20} />} 
          color="purple"
        />
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-3 gap-6 h-96">
        {/* Positions Table (2/3 width) */}
        <div className="col-span-2 bg-[#12161b]/80 border border-white/5 rounded-2xl shadow-2xl backdrop-blur-md overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-white/5 bg-white/[0.02]">
            <h3 className="text-sm font-bold text-gray-200 tracking-wider">ACTIVE POSITIONS</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="text-gray-500 font-semibold tracking-wider text-xs border-b border-white/5">
                  <th className="pb-3 px-2 font-medium uppercase">Instrument</th>
                  <th className="pb-3 px-2 font-medium uppercase">Type</th>
                  <th className="pb-3 px-2 font-medium uppercase text-right">Size</th>
                  <th className="pb-3 px-2 font-medium uppercase text-right">Open Price</th>
                  <th className="pb-3 px-2 font-medium uppercase text-right">Current Mark</th>
                  <th className="pb-3 px-2 font-medium uppercase text-right">Unrealized PnL</th>
                </tr>
              </thead>
              <tbody>
                {portfolio?.enriched_positions?.length > 0 ? (
                  portfolio.enriched_positions.map((pos) => (
                    <tr key={pos.trade_id} className="hover:bg-white/[0.02] transition-colors border-b border-white/[0.02] last:border-0">
                      <td className="py-3 px-2 text-gray-300 font-mono text-xs">{pos.instrument}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          pos.direction === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                        }`}>
                          {pos.direction}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right text-gray-300 font-mono">{pos.size}</td>
                      <td className="py-3 px-2 text-right text-gray-400 font-mono">${(pos.open_price).toFixed(4)}</td>
                      <td className="py-3 px-2 text-right text-gray-300 font-mono shadow-sm">${(pos.current_price).toFixed(4)}</td>
                      <td className={`py-3 px-2 text-right font-bold font-mono ${
                        pos.pnl > 0 ? 'text-emerald-400' : pos.pnl < 0 ? 'text-rose-400' : 'text-gray-400'
                      }`}>
                        {pos.pnl > 0 ? '+' : ''}${(pos.pnl * 100).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-gray-500 italic font-light">
                      No active options positions. Waiting for ML signal...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Logs Terminal (1/3 width) */}
        <div className="col-span-1 bg-[#0a0a0c] border border-white/10 rounded-2xl shadow-inner overflow-hidden flex flex-col relative group">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-50"></div>
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <Terminal size={14} className="text-blue-400" />
            <h3 className="text-[11px] font-bold text-gray-400 tracking-widest uppercase">Engine Logs</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 font-mono text-[10px] leading-relaxed text-gray-400 flex flex-col-reverse">
            <div>
              {logs.map((log, i) => {
                let color = "text-gray-400";
                if (log.includes("[ERROR]")) color = "text-red-400";
                if (log.includes("[WARNING]")) color = "text-amber-400";
                if (log.includes("TRADE_EXECUTED")) color = "text-emerald-400 font-bold";
                return <div key={i} className={`mb-1 ${color}`}>{log}</div>;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon, color, glow }) {
  const colorMap = {
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    red: "text-rose-400 bg-rose-500/10 border-rose-500/20",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  };
  
  const glowClass = glow ? `shadow-[0_0_15px_rgba(var(--${color}-rgb),0.15)]` : "";

  return (
    <div className={`p-5 rounded-2xl backdrop-blur-xl border ${colorMap[color]} ${glowClass} flex flex-col justify-between transition-all hover:scale-[1.02]`}>
      <div className="flex justify-between items-start mb-4">
        <span className="text-xs font-bold uppercase tracking-wider opacity-80">{title}</span>
        <div className="p-2 bg-white/5 rounded-lg">
          {icon}
        </div>
      </div>
      <div className="text-3xl font-black tracking-tight">{value}</div>
    </div>
  );
}
